import { useState, useEffect } from "react";
import { z } from "zod";
import { EntitySelector } from "./EntitySelector";
import { useAdmin } from "../context/AdminContext";

export interface EntityFormProps<T> {
  schema: z.ZodObject<z.ZodRawShape>;
  initialData?: Partial<T>;
  onSubmit: (data: T) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  customOptions?: Record<string, Array<{ id: string; label: string; metadata?: string }>>;
  excludeSelectors?: string[]; // Field keys to exclude from entity selector conversion
}

export function EntityForm<T extends Record<string, unknown>>({
  schema,
  initialData = {},
  onSubmit,
  onCancel,
  submitLabel = "Submit",
  customOptions,
  excludeSelectors = [],
}: EntityFormProps<T>) {
  const { adminApp } = useAdmin();
  const jsonSchema = z.toJSONSchema(schema, { io: "input" });
  const properties = jsonSchema.properties ?? {};
  const required = new Set(jsonSchema.required ?? []);

  const isObjectSchema = (prop: z.core.JSONSchema.JSONSchema) =>
    prop.type === "object" && prop.properties !== undefined;
  
  // Initialize form data with proper defaults for object fields
  const initializeFormData = (data: Partial<T>) => {
    const initialized: Record<string, unknown> = { ...data };
    
    // Auto-fill workspaceId if not provided
    if (!initialized.workspaceId && 'workspaceId' in schema.shape) {
      initialized.workspaceId = adminApp.workspaceId;
    }
    
    // Ensure metadata is an object if present
    Object.entries(properties).forEach(([key, prop]) => {
      // If it's an object type and not set, initialize as empty object
      if (typeof prop === "object" && isObjectSchema(prop) && initialized[key] === undefined) {
        initialized[key] = {};
      }
    });
    
    return initialized;
  };

  const [formData, setFormData] = useState<Record<string, unknown>>(initializeFormData(initialData));
  
  // Update workspaceId when adminApp.workspaceId changes
  useEffect(() => {
    if ('workspaceId' in schema.shape && !initialData.workspaceId) {
      setFormData(prev => ({ ...prev, workspaceId: adminApp.workspaceId }));
    }
  }, [adminApp.workspaceId, initialData.workspaceId, schema.shape]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Helper to determine if a field is an entity reference
  const getEntityType = (fieldKey: string): 'participant' | 'group' | 'thread' | 'chat-message' | 'workspace' | 'file' | 'tag' | null => {
    const lowerKey = fieldKey.toLowerCase();
    
    // Skip if explicitly excluded
    if (excludeSelectors.some(exclude => exclude.toLowerCase() === lowerKey)) return null;
    
    // Skip workspaceId as it's auto-filled
    if (lowerKey === 'workspaceid') return null;
    
    // Skip auto-generated IDs
    if (lowerKey === 'id' || lowerKey === 'participantid') return null;
    
    // Match specific patterns
    if (lowerKey === 'groupid') return 'group';
    if (lowerKey === 'threadid') return 'thread';
    if (lowerKey === 'messageid') return 'chat-message';
    if (lowerKey === 'fileid') return 'file';
    if (lowerKey === 'tagid') return 'tag';
    if (lowerKey.includes('participant') && lowerKey.includes('id')) return 'participant';
    
    return null;
  };

  // Extract field info from schema
  const fields = Object.entries(properties).map(([key, prop]) => {
    const field: {
      key: string;
      type: string;
      required: boolean;
      label: string;
      options?: string[];
      pattern?: string;
      min?: number;
      max?: number;
      default?: unknown;
    } = {
      key,
      type: 'text',
      required: required.has(key),
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
    };

    if (typeof prop !== "object") {
      return field;
    }

    if ("default" in prop) {
      field.default = prop.default;
    }

    // Determine field type (enum first: zod emits type "string" alongside enum)
    if (prop.enum !== undefined) {
      const values = prop.enum;
      if (values.every((value): value is string => typeof value === "string")) {
        field.type = 'select';
        field.options = values;
      }
    } else if (prop.type === "string") {
      field.type = 'text';
      
      // String formats (uuid/email/datetime) emit their own pattern; only keep explicit regexes
      if (prop.pattern !== undefined && prop.format === undefined) {
        field.pattern = prop.pattern;
      }
      if (prop.maxLength !== undefined) {
        field.max = prop.maxLength;
      }
      
      // Check if it's a datetime
      if (key.includes('At') || key.includes('time')) {
        field.type = 'datetime-local';
      }
      // Check if it's a UUID
      if (key.includes('Id') || key.includes('id')) {
        field.type = 'text';
      }
    } else if (prop.type === "number" || prop.type === "integer") {
      field.type = 'number';
      if (prop.minimum !== undefined) {
        field.min = prop.minimum;
      }
      if (prop.maximum !== undefined) {
        field.max = prop.maximum;
      }
    } else if (prop.type === "boolean") {
      field.type = 'checkbox';
    } else if (isObjectSchema(prop)) {
      // Handle object types (like metadata)
      field.type = 'json';
    }

    return field;
  });

  const handleChange = (key: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
    // Clear error for this field
    setErrors(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleJsonChange = (key: string, value: string) => {
    try {
      // Try to parse JSON, if empty string use empty object
      const parsed = value.trim() === '' ? {} : JSON.parse(value);
      handleChange(key, parsed);
    } catch (error) {
      // Keep the string value so user can continue editing
      handleChange(key, value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      // Validate with schema
      const validated = schema.parse(formData);
      
      setSubmitting(true);
      await onSubmit(validated as T);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.issues.forEach(err => {
          const path = err.path.join('.');
          fieldErrors[path] = err.message;
        });
        setErrors(fieldErrors);
      } else {
        console.error('Form submission error:', error);
        setErrors({ _global: error instanceof Error ? error.message : 'Unknown error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white dark:bg-gray-900 rounded-lg shadow">
      {errors._global && (
        <div className="p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
          {errors._global}
        </div>
      )}

      {fields.map(field => {
        // Skip auto-generated fields
        if (field.key === 'id' || field.key === 'createdAt' || field.key === 'updatedAt') {
          return null;
        }

        // Check if this field has custom dropdown options keyed by field name
        const fieldCustomOptions = customOptions?.[field.key];

        // Check if this is an entity reference field
        const entityType = getEntityType(field.key);
        const useEntitySelector = entityType && field.type === 'text';

        return (
          <div key={field.key}>
            {!useEntitySelector && !fieldCustomOptions && (
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}

            {fieldCustomOptions ? (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <select
                  value={String(formData[field.key] ?? '')}
                  onChange={e => handleChange(field.key, e.target.value)}
                  required={field.required}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select {field.label}…</option>
                  {fieldCustomOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}{opt.metadata ? ` — ${opt.metadata}` : ''}</option>
                  ))}
                </select>
              </>
            ) : useEntitySelector ? (
              <EntitySelector
                entityType={entityType}
                value={String(formData[field.key] ?? '')}
                onChange={(value) => handleChange(field.key, value)}
                required={field.required}
                label={field.label}
                placeholder={`Select ${entityType}...`}
                customOptions={customOptions?.[entityType]}
              />
            ) : field.type === 'select' && field.options ? (
              <select
                value={String(formData[field.key] ?? field.default ?? '')}
                onChange={e => handleChange(field.key, e.target.value)}
                required={field.required}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select...</option>
                {field.options.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : field.type === 'checkbox' ? (
              <input
                type="checkbox"
                checked={Boolean(formData[field.key] ?? field.default ?? false)}
                onChange={e => handleChange(field.key, e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            ) : field.type === 'number' ? (
              <input
                type="number"
                value={Number(formData[field.key] ?? field.default ?? '')}
                onChange={e => handleChange(field.key, Number(e.target.value))}
                required={field.required}
                min={field.min}
                max={field.max}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            ) : field.type === 'json' ? (
              <div>
                <textarea
                  value={typeof formData[field.key] === 'object' 
                    ? JSON.stringify(formData[field.key], null, 2) 
                    : String(formData[field.key] ?? '{}')}
                  onChange={e => handleJsonChange(field.key, e.target.value)}
                  required={field.required}
                  rows={4}
                  placeholder='{"key": "value"}'
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Enter valid JSON object (or leave as {'{}'} for empty)
                </p>
              </div>
            ) : (
              <input
                type={field.type}
                value={String(formData[field.key] ?? field.default ?? '')}
                onChange={e => handleChange(field.key, e.target.value)}
                required={field.required}
                pattern={field.pattern}
                maxLength={field.max}
                  readOnly={field.key === 'workspaceId' && !initialData.workspaceId}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 ${
                    field.key === 'workspaceId' && !initialData.workspaceId
                    ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' 
                    : 'bg-white dark:bg-gray-800'
                }`}
              />
            )}

            {errors[field.key] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors[field.key]}
              </p>
            )}
          </div>
        );
      })}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Submitting...' : submitLabel}
        </button>
      </div>
    </form>
  );
}

